import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Paper, Typography } from '@mui/material';
import BookingPaymentBreakdown, { BookingGstModeChip } from './BookingPaymentBreakdown';
import BookingExtraChargesOverlay from './BookingExtraChargesOverlay';

/**
 * Payment summary card with optional in-drawer extra charges overlay.
 */
export default function BookingPaymentSummaryCard({
  title = 'Payment Summary',
  summary,
  bookingId,
  isDark = false,
  drawerTheme = {},
  allowExtraChargesEdit = true,
  onExtraChargesSaved,
  overlayMountRef,
  paperSx = {},
  breakdownProps = {},
}) {
  const [extraChargesOpen, setExtraChargesOpen] = useState(false);

  const overlay = (
    <BookingExtraChargesOverlay
      open={extraChargesOpen}
      onClose={() => setExtraChargesOpen(false)}
      bookingId={bookingId}
      currency={summary?.currency}
      existingAmount={summary?.extraChargesAmount}
      existingBaseAmount={summary?.extraChargesBaseAmount}
      existingGstAmount={summary?.extraChargesGstAmount}
      existingNote={summary?.extraChargesNote}
      existingItems={summary?.extraChargesItems}
      roomGstPercent={summary?.gstPercentage}
      drawerContainerRef={overlayMountRef}
      isDark={isDark}
      onSaved={onExtraChargesSaved}
    />
  );

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          p: 1.5,
          mb: 2,
          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
          border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
          borderRadius: '12px',
          ...paperSx,
        }}
      >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: drawerTheme.text || (isDark ? '#ddd' : '#333'), fontWeight: 600 }}
        >
          {title}
        </Typography>
        {summary?.gstIncluded || summary?.gstAmount > 0 || summary?.displayGstAmount > 0 ? (
          <BookingGstModeChip
            inclusive={Boolean(summary?.inclusiveDisplay || summary?.taxInclusive)}
            isDark={isDark}
          />
        ) : null}
      </Box>

      <BookingPaymentBreakdown
        summary={summary}
        isDark={isDark}
        drawerTheme={drawerTheme}
        bookingId={bookingId}
        allowExtraChargesEdit={allowExtraChargesEdit}
        onAddExtraChargesClick={
          allowExtraChargesEdit && bookingId ? () => setExtraChargesOpen(true) : undefined
        }
        {...breakdownProps}
      />
    </Paper>
      {overlayMountRef?.current ? createPortal(overlay, overlayMountRef.current) : overlay}
    </>
  );
}
